import {CSSProperties, Fragment, useContext, useEffect, useRef, useState} from 'react';
import {Button} from '@/components/app/button';
import {ChoiceSelect} from '@/components/app/choice-select';
import {cn} from '@/lib/utils';
import {Icon} from '../../../app/components/Icon';
import {useGetBiotwinQuery, useLazyAskAIQuery} from '../../../app/services/biotwinApi';
import {TenantPatientInfoItem, TenantPatientInfoItemCodeType} from '../../tenant/types';
import {useGetTenantPatientInfoQuery} from '../../../app/services/tenantApi';
import {useIsAdmin} from '../../auth/hooks/useIsAdmin';
import {useTenantId} from '../../auth/hooks/useTenantId';
import {useGetConditionsQuery, useGetConditionTypesQuery} from '../../../app/services/conditionApi';
import {PersonContext} from '../../person/contexts/PersonContext';
import {Condition, ConditionTypeName} from '../types';
import {compareRelevantConditionsByType, getConditionTypeColorByName} from '../utils';
import styles from './IntegratedBiotwinPreviewTable.module.css';
interface Item {
  conditionName: string;
  conditionTypeName: ConditionTypeName;
  data: TenantPatientInfoItem;
  id: string;
}
interface Section {
  conditionTypeName: ConditionTypeName;
  items: Item[];
}
export interface IntegratedBiotwinPreviewTableProps {
  biotwinId: number;
  className?: string;
  onClose?: (event: {}) => void;
  onSave?: (event: {items: TenantPatientInfoItem[]}) => void;
}
export const IntegratedBiotwinPreviewTable = ({
  biotwinId,
  className,
  onClose,
  onSave,
}: IntegratedBiotwinPreviewTableProps) => {
  const {relevantConditionsRequiredRelated, relevantConditionsSelected} = useContext(PersonContext);
  const relevantConditions = [
    ...(relevantConditionsRequiredRelated ?? []),
    ...(relevantConditionsSelected ?? []),
  ].filter((rc) => !rc.isPatientSetting);
  const isAdmin = useIsAdmin();
  const {data: biotwin} = useGetBiotwinQuery({
    id: biotwinId,
  });
  const patientId = biotwin?.patientId ?? 0;
  const tenantId = useTenantId();
  const {data: patientInfo} = useGetTenantPatientInfoQuery(
    {
      patientId,
      tenantId,
    },
    {
      skip: !biotwinId || !patientId || !tenantId,
    },
  );
  const [collapsedItemIds, setCollapsedItemIds] = useState<string[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const willBeAddedItems: Item[] = items.filter((item) => selectedItemIds.includes(item.id));
  const [replacedConditionByItemId, setReplacedConditionByItemId] = useState<{
    [key: string]: Condition | null;
  }>({});
  const [sections, setSections] = useState<Section[]>([]);
  useEffect(() => {
    const listItems: Item[] = (patientInfo?.items ?? [])
      .map((item) => ({
        conditionName: item.condition?.conditionName ?? '',
        conditionTypeName: item.condition?.conditionTypeName ?? getConditionTypeNameFromCodeType(item.codeType),
        data: item,
        id: `patient-info-${item.code}-${item.codeName}`,
      }))
      .filter((item, i, a) => i === a.findIndex((item2) => item2.id === item.id))
      .filter((item) => isAdmin || item.conditionTypeName !== ConditionTypeName.Tests || item.data.condition != null);
    setItems(listItems);
    setSections(prepareSections(listItems));
  }, [isAdmin, patientInfo]);
  return (
    <div className={cn('flex-1 min-h-0 flex flex-col', className)}>
      <div className={'flex flex-1 min-h-0 h-full'}>
        <section className={'flex flex-col flex-1 min-w-0 rounded-2xl border border-gray-300'}>
          <header className={'flex-0 flex items-center p-4 border-b border-gray-300'}>
            <h1 className={'text-base m-0'}>Conditions</h1>
            <p className={'text-blue-400 text-sm my-0 ml-2'}>
              <Icon icon={'info-circle'} /> You can select the appropriate data from the list of HIS records below and
              save to transfer them into the Biotwin.
            </p>
          </header>
          <div
            className={'flex-1 overflow-auto pb-4 min-h-0'}
            style={{
              maxHeight: '28rem',
            }}
          >
            <table className={styles.table}>
              <tbody>
                {sections.map((section) => {
                  const {conditionTypeName, items} = section;
                  return (
                    <Fragment key={conditionTypeName}>
                      {headerTemplate(section)}
                      {!collapsedItemIds.includes(conditionTypeName) &&
                        items
                          .slice()
                          .sort(compareItems)
                          .map((item) => itemTemplate(item))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <div className={'shrink-0 flex justify-end gap-2 mt-6'}>
        {hasChanges() && (
          <Button onClick={() => reset()} type={'button'} variant="ghost" className="rounded-full">
            {'Reset'}
          </Button>
        )}
        <Button onClick={() => onClose?.({})} type={'button'} variant="ghost" className="rounded-full">
          {'Close Without Save'}
        </Button>
        <Button
          onClick={() =>
            onSave?.({
              items: willBeAddedItems.map((item) => item.data),
            })
          }
          type={'button'}
          className="rounded-full"
        >
          {'Save'}
        </Button>
      </div>
    </div>
  );
  function hasChanges() {
    return willBeAddedItems.length > 0;
  }
  function reset() {
    setSelectedItemIds([]);
  }
  function headerTemplate({conditionTypeName, items}: Section) {
    const color = getConditionTypeColorByName(conditionTypeName);
    const collapsed = collapsedItemIds.includes(conditionTypeName);
    const selectedItems = items.filter((item) => selectedItemIds.includes(item.id));
    return (
      <Fragment key={conditionTypeName}>
        <tr
          className={cn('sticky top-0 cursor-pointer')}
          onClick={() => {
            setCollapsedItemIds((prev) =>
              collapsed ? prev.filter((id) => id !== conditionTypeName) : [...prev, conditionTypeName],
            );
          }}
          style={{
            zIndex: 2,
          }}
        >
          <td className={cn(`text-${color ?? 'gray-500'}`, `bg-${color}-subtle`)} colSpan={2}>
            <div className={'flex items-center gap-4'}>
              <span className={'flex-1'}>{conditionTypeName || 'No Condition Type'}</span>
              {selectedItems.length > 0 && (
                <span className={'text-sm'}>
                  {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                </span>
              )}
              <span className={collapsed ? 'chevron-down' : 'chevron-up'} />
            </div>
          </td>
        </tr>
        {!collapsedItemIds.includes(conditionTypeName) && (
          <tr
            className={'sticky bg-white'}
            style={{
              top: '2.5rem',
              zIndex: 1,
            }}
          >
            <th>
              <div className={'pl-2'}>HIS Data</div>
            </th>
            <th>Biotandem Match</th>
          </tr>
        )}
      </Fragment>
    );
  }
  function itemTemplate(item: Item) {
    const {code, codeName, condition, date, isChronic, value} = item.data;
    const alreadyInBiotwin = condition != null && relevantConditions.some((rc) => condition.id === rc.conditionId);
    const unknown = codeName == null || codeName === '';
    const matched = condition != null;
    const selectable = !alreadyInBiotwin && !unknown && matched;
    const selected = selectedItemIds.includes(item.id);
    const selectedCondition = selected ? (replacedConditionByItemId[item.id] ?? condition) : null;
    const codeVisible = [ConditionTypeName.Diagnoses, ConditionTypeName.Tests].includes(item.conditionTypeName);
    return (
      <tr
        className={cn({
          'cursor-auto': !selectable,
          'cursor-pointer': selectable,
          'opacity-50': unknown || !matched,
          'text-gray-500': alreadyInBiotwin,
        })}
        key={item.id}
        onClick={() => {
          if (selectable) {
            if (selected && condition === selectedCondition) {
              setSelectedItemIds((prev) => prev.filter((id) => id !== item.id));
            } else {
              setSelectedItemIds((prev) => [...prev, item.id]);
            }
            setReplacedConditionByItemId((prev) => ({
              ...prev,
              [item.id]: null,
            }));
          }
        }}
      >
        <td
          className={'align-top'}
          style={{
            verticalAlign: 'top',
          }}
        >
          <div
            className={'flex items-center pl-2 gap-2'}
            style={{
              minHeight: '1.5rem',
            }}
          >
            <div
              className={cn('flex-1', {
                'text-green-600': selected,
              })}
            >
              {codeVisible && code} {codeName}
            </div>
            {value && <span className={'text-sm text-yellow-600'}>Result: {value}</span>}
            {!alreadyInBiotwin && isChronic && <div className={'text-sm text-yellow-600'}>Prevalent</div>}
            {alreadyInBiotwin && <span className={'text-sm text-muted-foreground'}>In Biotwin</span>}
            {date && (
              <div className={'text-sm text-muted-foreground'}>
                {new Date(date).toLocaleDateString('en', {
                  month: '2-digit',
                  year: 'numeric',
                })}
              </div>
            )}
          </div>
        </td>
        <td>
          <div
            className={cn('flex items-center gap-2', {
              'text-green-600': condition != null && condition === selectedCondition,
            })}
          >
            {selectable && <Icon icon={condition === selectedCondition ? 'check-square' : 'stop'} />}
            {condition?.conditionName || <div className={'font-light'}>No Proposed Biotandem Data</div>}
          </div>
          {selectable && condition?.conditionName && (
            <OtherSuggestions
              biotwinId={biotwinId}
              className={'ml-6 mt-2'}
              item={item}
              onChange={(condition) => {
                setReplacedConditionByItemId((prev) => ({
                  ...prev,
                  [item.id]: condition,
                }));
                if (selected && condition == null) {
                  setSelectedItemIds((prev) => prev.filter((id) => id !== item.id));
                }
                if (!selected && condition != null) {
                  setSelectedItemIds((prev) => [...prev, item.id]);
                }
              }}
              value={replacedConditionByItemId[item.id] ?? null}
            />
          )}
          {!condition?.conditionName && <AISuggestions biotwinId={biotwinId} className={'ml-6 mt-2'} item={item} />}
        </td>
      </tr>
    );
  }
  function compareItems(a: Item, b: Item) {
    return (
      compareRelevantConditionsByType(a.conditionTypeName, b.conditionTypeName) ||
      Number(selectedItemIds.includes(b.id)) - Number(selectedItemIds.includes(a.id)) ||
      Number(selectedItemIds.indexOf(a.id)) - Number(selectedItemIds.indexOf(b.id)) ||
      Number(b.data.isChronic) - Number(a.data.isChronic) ||
      Number(b.data.condition != null) - Number(a.data.condition != null) ||
      Number(!!b.data.codeName) - Number(!!a.data.codeName) ||
      a.data.code.localeCompare(b.data.code) ||
      a.conditionName.localeCompare(b.conditionName)
    );
  }
};
function prepareSections(items: Item[]) {
  const conditionTypeNames = Array.from(new Set(items.map((item) => item.conditionTypeName)));
  return conditionTypeNames.map((conditionTypeName) => ({
    conditionTypeName,
    items: items.filter((item) => item.conditionTypeName === conditionTypeName),
  }));
}
function getConditionTypeNameFromCodeType(codeType: TenantPatientInfoItemCodeType) {
  switch (codeType) {
    case TenantPatientInfoItemCodeType.Disease:
      return ConditionTypeName.Diagnoses;
    case TenantPatientInfoItemCodeType.Drug:
      return ConditionTypeName.Medications;
    case TenantPatientInfoItemCodeType.FamilyHistory:
      return ConditionTypeName.FamilyHistory;
    case TenantPatientInfoItemCodeType.Allergy:
      return ConditionTypeName.Allergy;
    case TenantPatientInfoItemCodeType.Test:
      return ConditionTypeName.Tests;
    default:
      return ConditionTypeName.Diagnoses;
  }
}

// Other Suggestions

interface OtherSuggestionsProps {
  biotwinId: number;
  className?: string;
  item: Item;
  onChange: (value: Condition | null) => void;
  style?: CSSProperties;
  value?: Condition | null;
}
const OtherSuggestions = ({biotwinId, className, item, onChange, style, value}: OtherSuggestionsProps) => {
  const {data: conditionTypes} = useGetConditionTypesQuery();
  const {data: familyHistoryConditions} = useGetConditionsQuery(
    {
      conditionTypeId: conditionTypes?.find((ct) => ct.conditionTypeName === ConditionTypeName.FamilyHistory)?.id,
      includeGroups: true,
      pageNumber: 0,
      pageSize: 1000,
      searchTerm: '',
      userId: biotwinId,
    },
    {
      skip: !biotwinId || !conditionTypes?.length,
    },
  );
  const suggestions = familyHistoryConditions?.filter((c) => c.parentLabelId === item.data.condition?.id);
  if (!suggestions?.length) {
    return null;
  }
  return (
    <div className={cn('flex flex-col gap-2', className)} style={style}>
      {suggestions.map((suggestion) => {
        const selected = value?.id === suggestion.id;
        return (
          <div
            className={cn('flex items-center gap-2', {
              'text-green-600': selected,
            })}
            onClick={(e) => {
              onChange?.(selected ? null : suggestion);
              e.stopPropagation();
            }}
            key={suggestion.id}
            style={{
              cursor: 'pointer',
            }}
          >
            <Icon icon={value === suggestion ? 'check-square' : 'stop'} />
            {suggestion.conditionName}
          </div>
        );
      })}
    </div>
  );
};

// AI Suggestions

interface AISuggestionsProps {
  biotwinId: number;
  className?: string;
  item: Item;
  style?: CSSProperties;
}
const AISuggestions = ({biotwinId, className, item, style}: AISuggestionsProps) => {
  return null;
};
